import * as fabricTypes from 'fabric'

export enum FillType {
  Solid = 'solid',
  Linear = 'linear',
  Radial = 'radial',
}

export interface GradientPoint {
  color: string
  offset: number
}

export interface Rgba {
  r: number
  g: number
  b: number
  a: number
}

export interface Hsl {
  h: number
  s: number
  l: number
  alpha: number
}

interface Hsb {
  h: number
  s: number
  b: number
  a: number
}

export interface ColorPickerLinearData {
  mode: FillType.Linear
  points: GradientPoint[]
  degree: number
}

export interface ColorPickerRadialData {
  mode: FillType.Radial
  points: GradientPoint[]
}

export interface ColorPickerSolidData {
  mode: FillType.Solid
  color: string
}

export interface FabricGradientOptions extends fabricTypes.GradientOptions<FillType.Radial | FillType.Linear> {
  deg?: number
  style?: string
  x1Coeff?: number
  x2Coeff?: number
  y1Coeff?: number
  y2Coeff?: number
}

export const isHex = (color: string): boolean => {
  let hex = color
  if (color.indexOf('#') === 0) {
    hex = color.slice(1)
  }
  return /^([0-9A-F]{3}){1,2}$/i.test(hex)
}

export const rgbaToHex = (rgba: string): string => {
  if (isHex(rgba)) {
    return rgba
  }
  const rgb = parseRgba(rgba)
  return rgb ? '#' +
    (rgb.r | 1 << 8).toString(16).slice(1) +
    (rgb.g | 1 << 8).toString(16).slice(1) +
    (rgb.b | 1 << 8).toString(16).slice(1) : rgba
}

export const parseRgba = (rgba: string): Rgba => {
  if (isHex(rgba)) {
    let hex = rgba.replace('#', '')

    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
    }

    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16),
      a: 1,
    }
  }
  const rgb = rgba.replace(/\s/g, '')
    .match(/^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i) ?? [ '', '255', '255', '255', '1' ]
  return {
    r: parseInt(rgb[1]),
    g: parseInt(rgb[2]),
    b: parseInt(rgb[3]),
    a: parseFloat(rgb[4]),
  }
}

export const stringColorToHSL = (color: string): Hsl => {
  const rgba = parseRgba(color)

  return rgbaToHsl(rgba)
}

export const rgbaToHsl = (rgba: Rgba): Hsl => {
  // Normalize R, G, B values to the range [0, 1]
  const r = rgba.r / 255
  const g = rgba.g / 255
  const b = rgba.b / 255

  // Find the maximum and minimum values among R, G, B
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)

  let h = 0, s = 0

  // Calculate Lightness (L)
  const l = (max + min) / 2

  // If max and min are equal, the color is achromatic (grayscale)
  if (max === min) {
    h = 0 // Hue is undefined for achromatic colors, set to 0
    s = 0 // Saturation is 0
  } else {
    const d = max - min // Delta

    // Calculate Saturation (S)
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    // Calculate Hue (H)
    switch (max) {
    case r:
      h = (g - b) / d + (g < b ? 6 : 0)
      break
    case g:
      h = (b - r) / d + 2
      break
    case b:
      h = (r - g) / d + 4
      break
    }
    h /= 6 // Normalize hue to the range [0, 1]
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
    alpha: rgba.a * 100,
  }
}

export const hslToRgba = (color: Hsl): Rgba => {
  // Ensure saturation and lightness are between 0 and 1
  const s = color.s / 100
  const l = color.l / 100
  const h = color.h

  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l - c / 2
  let r = 0
  let g = 0
  let b = 0

  if (0 <= h && h < 60) {
    r = c
    g = x
    b = 0
  } else if (60 <= h && h < 120) {
    r = x
    g = c
    b = 0
  } else if (120 <= h && h < 180) {
    r = 0
    g = c
    b = x
  } else if (180 <= h && h < 240) {
    r = 0
    g = x
    b = c
  } else if (240 <= h && h < 300) {
    r = x
    g = 0
    b = c
  } else if (300 <= h && h <= 360) {
    r = c
    g = 0
    b = x
  }

  r = Math.round((r + m) * 255)
  g = Math.round((g + m) * 255)
  b = Math.round((b + m) * 255)

  return { r, g, b, a: color.alpha / 100 }
}

export const rgbToHsb = (rgba: Rgba) => {
  const hsba: Hsb = { h: 0, s: 0, b: 0, a: rgba.a }
  const min = Math.min(rgba.r, rgba.g, rgba.b)
  const max = Math.max(rgba.r, rgba.g, rgba.b)
  const delta = max - min
  hsba.b = max
  hsba.s = max != 0 ? 255 * delta / max : 0
  if (hsba.s != 0) {
    if (rgba.r == max)
      hsba.h = (rgba.g - rgba.b) / delta
    else if (rgba.g == max)
      hsba.h = 2 + (rgba.b - rgba.r) / delta
    else
      hsba.h = 4 + (rgba.r - rgba.g) / delta
  } else
    hsba.h = -1
  hsba.h *= 60
  if (hsba.h < 0)
    hsba.h += 360
  hsba.s *= 100 / 255
  hsba.b *= 100 / 255
  return hsba
}

export const hsbToRgb = (hsb: Hsb) => {
  const rgb: Rgba = { r: 0, g: 0, b: 0, a: hsb.a }
  let h = Math.round(hsb.h)
  const s = Math.round(hsb.s * 255 / 100)
  const v = Math.round(hsb.b * 255 / 100)
  if (s == 0) {
    rgb.r = rgb.g = rgb.b = v
  } else {
    const t1 = v
    const t2 = (255 - s) * v / 255
    const t3 = (t1 - t2) * (h % 60) / 60
    if (h == 360)
      h = 0
    if (h < 60) {
      rgb.r = t1
      rgb.b = t2
      rgb.g = t2 + t3
    } else if (h < 120) {
      rgb.g = t1
      rgb.b = t2
      rgb.r = t1 - t3
    } else if (h < 180) {
      rgb.g = t1
      rgb.r = t2
      rgb.b = t2 + t3
    } else if (h < 240) {
      rgb.b = t1
      rgb.r = t2
      rgb.g = t1 - t3
    } else if (h < 300) {
      rgb.b = t1
      rgb.g = t2
      rgb.r = t2 + t3
    } else if (h < 360) {
      rgb.r = t1
      rgb.g = t2
      rgb.b = t1 - t3
    } else {
      rgb.r = 0
      rgb.g = 0
      rgb.b = 0
    }
  }
  return { r: Math.round(rgb.r), g: Math.round(rgb.g), b: Math.round(rgb.b), a: rgb.a }
}

export const makeRgba = (rgba: Rgba): string => {
  return `rgba(${rgba.r},${rgba.g},${rgba.b},${rgba.a})`
}

export const parseColorPickerData = (value: ColorPickerLinearData | ColorPickerRadialData | ColorPickerSolidData): {
  type: FillType
  data: FabricGradientOptions | string
} => {
  if (value.mode !== FillType.Solid) {
    const options: FabricGradientOptions = {
      type: value.mode,
      colorStops: [],
      // style: value.css,
    }
    value.points.forEach((point) => {
      options.colorStops?.push({
        offset: point.offset / 100,
        color: point.color,
      })
    })

    if (value.mode === FillType.Linear) {
      options.deg = value.degree
      return {
        type: value.mode,
        data: Object.assign(options, calculateLinearCoefficient(value.degree)),
      }
    }

    return {
      type: value.mode,
      data: options,
    }
  }
  return {
    type: FillType.Solid,
    data: value.color,
  }
}

export const fabricDataToColorPickerData = (
  data: FabricGradientOptions | string,
): ColorPickerLinearData | ColorPickerRadialData | ColorPickerSolidData => {
  if (typeof data !== 'string') {
    const options = {
      degree: 90,
      mode: data.type ?? FillType.Linear,
      points: [] as GradientPoint[],
    }
    data.colorStops?.forEach((point) => {
      options.points.push({
        offset: point.offset * 100,
        color: point.color,
      })
    })
    if (options.mode === FillType.Linear) {
      options.degree = data.deg ?? 90
    }

    return options as ColorPickerLinearData | ColorPickerRadialData
  }

  return {
    mode: FillType.Solid,
    color: data,
  }
}

const calculateLinearCoefficient = (deg: number): {
  x1Coeff: number
  x2Coeff: number
  y1Coeff: number
  y2Coeff: number
} => {
  const x1 = (
    (deg <= 45) ?
      (Math.abs(45 - deg) / 90) :
      (deg <= 135) ?
        0 :
        (deg <= 225) ?
          (1 - (Math.abs(225 - deg) / 90)) :
          (deg <= 315) ?
            1 :
            (1 - (Math.abs(315 - deg) / 90))
  )
  const y1 = (
    (deg <= 45 || deg >= 315) ?
      1 :
      (
        (deg >= 135 && deg <= 225) ?
          0 :
          (Math.abs(180 - deg) / 180)
      )
  )
  return {
    x1Coeff: x1,
    y1Coeff: y1,
    x2Coeff: 1 - x1,
    y2Coeff: 1 - y1,
  }
}

export const getCssStypeFromPoints = (data: ColorPickerLinearData | ColorPickerRadialData): string => {
  let style: string
  if (data.mode === FillType.Linear) {
    style = `linear-gradient(${data.degree / 360}turn, `
  } else {
    style = 'radial-gradient('
  }
  const parts: string[] = []
  data.points.forEach((point) => {
    parts.push(`${point.color} ${point.offset}%`)
    // style += `, ${point.color} ${point.offset}%`
  })
  return `${style}${parts.join(', ')})`
}
